import React, { lazy, useEffect, useState } from 'react'
import { Navigate, Route, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import { useSalesforceContext } from '../../SalesforceApp'
import { NoHeaderLayoutWrapper } from '../RoutingApp'
import { getCRMFormData } from '../../modules/crmAuth/actions'
import { isCanvasMigrationMockEnabled } from './canvasMigrationMock'
import {
  CANVAS_MIGRATION_PATH,
  shouldHideAppChromeForCanvasMigration,
} from './canvasMigrationPaths'
import { isCanvasMigrationExperienceActive } from './canvasMigrationTypes'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'

const CanvasMigrationPanel = lazy(() =>
  import('./CanvasMigrationPanel').then(module => ({
    default: module.CanvasMigrationPanel,
  }))
)

const DEFAULT_ONBOARDING_HOME = '/overview'

const inactiveMigrationLanding = {
  isEnabled: false,
  isStatusLoading: false,
  hideAppChrome: false,
  completedOnboardingHome: DEFAULT_ONBOARDING_HOME,
  dbStatus: null as string | null,
}

export type CanvasMigrationLanding = {
  /** Whether the temporary migration experience replaces Canvas Labra Home. */
  isEnabled: boolean
  /** True while CRM schema (and therefore migration status) is still loading. */
  isStatusLoading: boolean
  /** Hide the sidebar and help widget on migration-owned routes. */
  hideAppChrome: boolean
  /** Where Canvas should send the partner after login / home. */
  completedOnboardingHome: string
  /** Raw DB status, or null when the field is absent. */
  dbStatus: string | null
}

/**
 * Migration console state for Salesforce Canvas only.
 *
 * Admin sets `crm_schema.canvas_migration_status`; partners see the UI when
 * Labra Home loads inside Salesforce Canvas — not on the Labra webapp shell.
 */
export const useCanvasMigrationLanding = (): CanvasMigrationLanding => {
  const location = useLocation()
  const dispatch =
    useDispatch<ThunkDispatch<CrmOnboardingRootState, unknown, AnyAction>>()
  const { isInSalesforceContext, crmId: salesforceCrmId } =
    useSalesforceContext()
  const partnerCrmId = useSelector(
    (state: CrmOnboardingRootState) =>
      state.AccountData?.user?.partnerData?.crmId || ''
  )
  const crmId = partnerCrmId || salesforceCrmId || ''
  const crmFormData = useSelector(
    (state: CrmOnboardingRootState) => state.CRMAuth.crmFormData
  )
  // Cleared only after getCRMFormData settles (success or failure) so a failed
  // fetch cannot leave SalesforceCanvasLoader on LoadingAuthPage forever.
  const [hasResolvedCrmStatus, setHasResolvedCrmStatus] = useState(false)

  const hasMatchingCrmForm =
    Boolean(crmId) && crmFormData?.crm_id === crmId && !isEmpty(crmFormData)

  useEffect(() => {
    if (
      !isInSalesforceContext ||
      !crmId ||
      isCanvasMigrationMockEnabled ||
      hasMatchingCrmForm
    ) {
      setHasResolvedCrmStatus(true)
      return
    }

    let isCancelled = false
    setHasResolvedCrmStatus(false)

    void Promise.resolve(
      dispatch(
        getCRMFormData(crmId, {
          errorPresentation: 'none',
        })
      )
    ).finally(() => {
      if (!isCancelled) {
        setHasResolvedCrmStatus(true)
      }
    })

    return () => {
      isCancelled = true
    }
  }, [crmId, dispatch, hasMatchingCrmForm, isInSalesforceContext])

  if (!isInSalesforceContext) {
    return inactiveMigrationLanding
  }

  const dbStatus = isCanvasMigrationMockEnabled
    ? 'pending'
    : (crmFormData?.crm_schema?.canvas_migration_status ?? null)

  const isEnabled = isCanvasMigrationExperienceActive(dbStatus)
  const isStatusLoading =
    !isCanvasMigrationMockEnabled &&
    Boolean(crmId) &&
    !hasMatchingCrmForm &&
    !hasResolvedCrmStatus

  return {
    isEnabled,
    isStatusLoading,
    hideAppChrome: shouldHideAppChromeForCanvasMigration({
      isMigrationActive: isEnabled,
      pathname: location.pathname,
    }),
    completedOnboardingHome: isEnabled
      ? CANVAS_MIGRATION_PATH
      : DEFAULT_ONBOARDING_HOME,
    dbStatus: typeof dbStatus === 'string' ? dbStatus : null,
  }
}

/**
 * Returns the migration routes as a fragment for inclusion in the app's
 * `<Routes>`. When active, these routes also steal `/overview` for the Canvas
 * home experience. When inactive, keep `/canvas-migration` so a just-completed
 * status write redirects to overview instead of falling through to PageNotFound.
 */
export const renderCanvasMigrationRoutes = (isEnabled: boolean) => {
  if (!isEnabled) {
    return (
      <Route
        path={CANVAS_MIGRATION_PATH}
        element={<Navigate to={DEFAULT_ONBOARDING_HOME} replace />}
      />
    )
  }

  return (
    <>
      <Route
        path={CANVAS_MIGRATION_PATH}
        element={
          <NoHeaderLayoutWrapper
            component={CanvasMigrationPanel}
            isSidePanelOpened={false}
            isExternal
          />
        }
      />
      <Route
        path="/overview/*"
        element={<Navigate to={CANVAS_MIGRATION_PATH} replace />}
      />
      <Route
        path="/dashboard/overview"
        element={<Navigate to={CANVAS_MIGRATION_PATH} replace />}
      />
      <Route
        path="/dashboard/:cloud/:accountId/overview"
        element={<Navigate to={CANVAS_MIGRATION_PATH} replace />}
      />
      <Route
        path="/dashboard/:cloud/:accountId/overview/*"
        element={<Navigate to={CANVAS_MIGRATION_PATH} replace />}
      />
    </>
  )
}
